// src/services/inventoryAPI.ts
// UPDATED: Centralized auth + full Inventory API + Bulk Upload + Centralized Logging + Environment Config
// Phase 1 Cleanup: Order status extension removed - ordering now handled via Purchases workflow

// 🔐 Centralized auth service
import { authService } from './authService';

// 📊 Centralized logging
import { logger } from '../utils/logger';

// 🔧 Environment configuration
import { getApiBaseUrl } from './configService';

// 📤 Bulk Upload types
import {
  BulkUploadItem,
  BulkUploadResult,
  TemplateType
} from '../types/bulkInventory';

// Create service-specific logger
const inventoryLogger = logger.createServiceLogger('InventoryAPI');

// Types for API responses
export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

// Base inventory item interface
export interface BaseInventoryItem {
  tenantId: string;
  itemId: string;
  name: string;
  description?: string;
  category: string;
  manufacturer?: string;
  model?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  location: string;
  subLocation?: string;
  condition: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Needs Repair';
  photos: string[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

// Serialized item interface
export interface SerializedInventoryItem extends BaseInventoryItem {
  itemType: 'SERIALIZED';
  serialNumber: string;
  status: 'Available' | 'Assigned' | 'Maintenance' | 'Retired';
  assignedTo?: string;
  assignedDate?: string;
  assignedBy?: string;
}

// Bulk item interface
export interface BulkInventoryItem extends BaseInventoryItem {
  itemType: 'BULK_TRACKED' | 'CONSUMABLE';
  totalQuantity: number;
  availableQuantity: number;
  minStockLevel?: number;
}

// Body armor specific fields
export interface BodyArmorFields {
  ballisticLevel: string;
  size: string;
  expirationDate: string;
  lastInspectionDate?: string;
  nextInspectionDue?: string;
}

// Body armor item (extends serialized item)
export interface BodyArmorInventoryItem extends SerializedInventoryItem {
  bodyArmor: BodyArmorFields;
}

// Union type for all inventory items
export type InventoryItem =
  | SerializedInventoryItem
  | BulkInventoryItem
  | BodyArmorInventoryItem;

// Request types for creating items
export interface CreateSerializedItemRequest {
  itemType: 'SERIALIZED';
  name: string;
  description?: string;
  category: string;
  manufacturer?: string;
  model?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  location: string;
  subLocation?: string;
  condition: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Needs Repair';
  serialNumber: string;
  status: 'Available' | 'Assigned' | 'Maintenance' | 'Retired';
  assignedTo?: string;
  assignedDate?: string;
  photos?: File[];
  bodyArmor?: BodyArmorFields;
}

export interface CreateBulkItemRequest {
  itemType: 'BULK';
  name: string;
  description?: string;
  category: string;
  manufacturer?: string;
  model?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  location: string;
  subLocation?: string;
  condition: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Needs Repair';
  totalQuantity: number;
  availableQuantity: number;
  minStockLevel?: number;
  photos?: File[];
}

export type CreateItemRequest = CreateSerializedItemRequest | CreateBulkItemRequest;

// Update request type
export interface UpdateItemRequest {
  name?: string;
  description?: string;
  category?: string;
  manufacturer?: string;
  model?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  location?: string;
  subLocation?: string;
  condition?: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Needs Repair';
  serialNumber?: string;
  status?: 'Available' | 'Assigned' | 'Maintenance' | 'Retired';
  assignedTo?: string;
  assignedDate?: string;
  totalQuantity?: number;
  availableQuantity?: number;
  minStockLevel?: number;
  bodyArmor?: BodyArmorFields;
  photos?: File[];
}

// API service class
export class InventoryAPI {
  private baseUrl: string;

  constructor() {
    // Get base URL from environment configuration
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * 🚀 Uses centralized authService for all requests
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const endTimer = logger.startTimer('InventoryAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('InventoryAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    // If we're sending JSON and no header is set, add it (authService will handle Authorization)
    const hasBody = options.body !== undefined && options.body !== null;
    const headers = new Headers(options.headers || {});
    if (hasBody && !headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        { ...options, headers }
      );

      logger.logAPIResponse('InventoryAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      // Some endpoints (like export) return blobs; most return JSON
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json()
        : (undefined as unknown as APIResponse<T>);

      if (!response.ok) {
        inventoryLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        return {
          success: false,
          error:
            (data && (data as any).error) || {
              code: 'HTTP_ERROR',
              message: `HTTP ${response.status}`,
            },
        };
      }

      // When the response isn't JSON (e.g., blob endpoints handled separately),
      // return a generic success wrapper.
      return (data ?? { success: true }) as APIResponse<T>;
    } catch (error: any) {
      endTimer();
      inventoryLogger.error('Request failed', { endpoint, method: options.method }, error);

      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' },
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' },
        };
      }

      if (error.code === 'NETWORK_ERROR') {
        return {
          success: false,
          error: { code: 'NETWORK_ERROR', message: error.message },
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' },
      };
    }
  }

  // ===== Core Inventory CRUD & Queries =====

  // Get all inventory items
  async getItems(): Promise<APIResponse<InventoryItem[]>> {
    inventoryLogger.debug('Fetching all inventory items');
    return this.makeRequest<InventoryItem[]>('/inventory');
  }

  // Get single inventory item
  async getItem(itemId: string): Promise<APIResponse<InventoryItem>> {
    inventoryLogger.debug('Fetching inventory item', { itemId });
    return this.makeRequest<InventoryItem>(`/inventory/${itemId}`);
  }

  // Create new inventory item
  async createItem(itemData: CreateItemRequest): Promise<APIResponse<InventoryItem>> {
    inventoryLogger.info('Creating new inventory item', { 
      itemType: itemData.itemType, 
      name: itemData.name,
      category: itemData.category 
    });
    return this.makeRequest<InventoryItem>('/inventory', {
      method: 'POST',
      body: JSON.stringify({ item: itemData }),
    });
  }

  // Update existing inventory item
  async updateItem(
    itemId: string,
    itemData: UpdateItemRequest
  ): Promise<APIResponse<InventoryItem>> {
    inventoryLogger.info('Updating inventory item', { 
      itemId,
      fieldsUpdated: Object.keys(itemData)
    });
    return this.makeRequest<InventoryItem>(`/inventory/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify({ item: itemData }),
    });
  }

  // Delete inventory item
  async deleteItem(itemId: string): Promise<APIResponse<void>> {
    inventoryLogger.warn('Deleting inventory item', { itemId });
    return this.makeRequest<void>(`/inventory/${itemId}`, {
      method: 'DELETE',
    });
  }

  // Upload photos (with authentication)
  async uploadPhotos(itemId: string, files: File[]): Promise<APIResponse<string[]>> {
    inventoryLogger.info('Uploading photos', { 
      itemId, 
      fileCount: files.length,
      totalSize: files.reduce((sum, f) => sum + f.size, 0)
    });

    try {
      const token = await authService.getToken();

      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`photo_${index}`, file);
      });
      formData.append('itemId', itemId);

      const response = await fetch(`${this.baseUrl}/inventory/${itemId}/photos`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Content-Type not set for FormData
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        inventoryLogger.error('Photo upload failed', { itemId, status: response.status });
        return {
          success: false,
          error: data.error || { code: 'UPLOAD_ERROR', message: 'Photo upload failed' },
        };
      }

      inventoryLogger.info('Photos uploaded successfully', { itemId, photoCount: data.photoUrls?.length });
      return {
        success: true,
        data: data.photoUrls || [],
      };
    } catch (error: any) {
      inventoryLogger.error('Photo upload error', { itemId }, error);
      return {
        success: false,
        error: { code: 'UPLOAD_ERROR', message: error.message },
      };
    }
  }

  // Search inventory items
  async searchItems(
    query: string,
    filters?: {
      category?: string;
      status?: string;
      location?: string;
      condition?: string;
    }
  ): Promise<APIResponse<InventoryItem[]>> {
    inventoryLogger.debug('Searching inventory items', { 
      query: query.substring(0, 50), // Truncate for logging
      filters 
    });

    const params = new URLSearchParams();
    params.append('q', query);

    if (filters) {
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.location) params.append('location', filters.location);
      if (filters.condition) params.append('condition', filters.condition);
    }

    return this.makeRequest<InventoryItem[]>(`/inventory/search?${params.toString()}`);
  }

  // Get items by category
  async getItemsByCategory(
    category: string,
    subcategory?: string
  ): Promise<APIResponse<InventoryItem[]>> {
    inventoryLogger.debug('Fetching items by category', { category, subcategory });

    const params = new URLSearchParams();
    params.append('category', category);
    if (subcategory) params.append('subcategory', subcategory);

    return this.makeRequest<InventoryItem[]>(`/inventory/by-category?${params.toString()}`);
  }

  // Get items expiring soon (for body armor)
  async getExpiringSoon(days: number = 30): Promise<APIResponse<BodyArmorInventoryItem[]>> {
    inventoryLogger.debug('Fetching items expiring soon', { days });
    return this.makeRequest<BodyArmorInventoryItem[]>(`/inventory/expiring?days=${days}`);
  }

  // Get low stock items (for bulk items)
  async getLowStockItems(): Promise<APIResponse<BulkInventoryItem[]>> {
    inventoryLogger.debug('Fetching low stock items');
    return this.makeRequest<BulkInventoryItem[]>('/inventory/low-stock');
  }

  // Get items by status
  async getItemsByStatus(
    status: 'Available' | 'Assigned' | 'Maintenance' | 'Retired'
  ): Promise<APIResponse<InventoryItem[]>> {
    inventoryLogger.debug('Fetching items by status', { status });
    return this.makeRequest<InventoryItem[]>(`/inventory/by-status?status=${status}`);
  }

  // Get items by location
  async getItemsByLocation(
    location: string,
    subLocation?: string
  ): Promise<APIResponse<InventoryItem[]>> {
    inventoryLogger.debug('Fetching items by location', { location, subLocation });

    const params = new URLSearchParams();
    params.append('location', location);
    if (subLocation) params.append('subLocation', subLocation);

    return this.makeRequest<InventoryItem[]>(`/inventory/by-location?${params.toString()}`);
  }

  // Get inventory summary/statistics
  async getInventorySummary(): Promise<
    APIResponse<{
      totalItems: number;
      availableItems: number;
      assignedItems: number;
      maintenanceItems: number;
      retiredItems: number;
      lowStockItems: number;
      expiringSoon: number;
      byCategory: Record<string, number>;
      byLocation: Record<string, number>;
      byCondition: Record<string, number>;
    }>
  > {
    inventoryLogger.debug('Fetching inventory summary');
    return this.makeRequest('/inventory/summary');
  }

  // Bulk operations
  async bulkUpdateStatus(
    itemIds: string[],
    status: string,
    reason?: string
  ): Promise<
    APIResponse<{
      updated: string[];
      errors: Array<{ itemId: string; error: string }>;
    }>
  > {
    inventoryLogger.info('Bulk updating item status', { 
      itemCount: itemIds.length, 
      status,
      reason 
    });
    return this.makeRequest('/inventory/bulk-update-status', {
      method: 'POST',
      body: JSON.stringify({ itemIds, status, reason }),
    });
  }

  async bulkUpdateLocation(
    itemIds: string[],
    location: string,
    subLocation?: string
  ): Promise<
    APIResponse<{
      updated: string[];
      errors: Array<{ itemId: string; error: string }>;
    }>
  > {
    inventoryLogger.info('Bulk updating item location', { 
      itemCount: itemIds.length, 
      location, 
      subLocation 
    });
    return this.makeRequest('/inventory/bulk-update-location', {
      method: 'POST',
      body: JSON.stringify({ itemIds, location, subLocation }),
    });
  }

  // Export inventory data
  async exportInventory(filters?: {
    category?: string;
    status?: string;
    location?: string;
    format?: 'csv' | 'excel';
  }): Promise<Blob> {
    inventoryLogger.info('Exporting inventory data', { filters });

    const params = new URLSearchParams();
    if (filters) {
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.location) params.append('location', filters.location);
      params.append('format', filters.format || 'csv');
    }

    const response = await authService.makeAuthenticatedRequest(
      `${this.baseUrl}/inventory/export?${params.toString()}`
    );

    if (!response.ok) {
      inventoryLogger.error('Export failed', { status: response.status, statusText: response.statusText });
      throw new Error(`Export failed: ${response.statusText}`);
    }

    inventoryLogger.info('Export completed successfully');
    return response.blob();
  }

  // Import inventory data
  async importInventory(
    file: File,
    options?: {
      skipDuplicates?: boolean;
      updateExisting?: boolean;
    }
  ): Promise<
    APIResponse<{
      imported: number;
      skipped: number;
      errors: Array<{ row: number; error: string }>;
    }>
  > {
    inventoryLogger.info('Importing inventory data', { 
      fileName: file.name,
      fileSize: file.size,
      options 
    });

    try {
      const token = await authService.getToken();

      const formData = new FormData();
      formData.append('file', file);
      if (options) {
        formData.append('options', JSON.stringify(options));
      }

      const response = await fetch(`${this.baseUrl}/inventory/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Content-Type not set for FormData
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        inventoryLogger.error('Import failed', { fileName: file.name, status: response.status });
        return {
          success: false,
          error: data.error || { code: 'IMPORT_ERROR', message: 'Import failed' },
        };
      }

      inventoryLogger.info('Import completed', { 
        fileName: file.name,
        imported: data.imported,
        skipped: data.skipped,
        errors: data.errors?.length 
      });
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      inventoryLogger.error('Import error', { fileName: file.name }, error);
      return {
        success: false,
        error: { code: 'IMPORT_ERROR', message: error.message },
      };
    }
  }

  // ===== BULK UPLOAD OPERATIONS =====

  /**
   * Download CSV template for bulk upload
   */
  async getSampleCSV(type: TemplateType): Promise<Blob> {
    inventoryLogger.info('Downloading CSV template', { type });

    const response = await authService.makeAuthenticatedRequest(
      `${this.baseUrl}/inventory/sample-csv?type=${type}`
    );

    if (!response.ok) {
      inventoryLogger.error('Template download failed', { type, status: response.status });
      throw new Error(`Template download failed: ${response.statusText}`);
    }

    inventoryLogger.info('Template downloaded successfully', { type });
    return response.blob();
  }

  /**
   * Bulk upload inventory items from CSV
   */
  async bulkUploadInventory(items: BulkUploadItem[]): Promise<APIResponse<BulkUploadResult>> {
    // Get unique categories using Array.from instead of spread operator for TypeScript compatibility
    const categorySet = new Set(items.map(i => i.category));
    const uniqueCategories = Array.from(categorySet);
    
    inventoryLogger.info('Bulk uploading inventory items', { 
      itemCount: items.length,
      categories: uniqueCategories
    });

    return this.makeRequest<BulkUploadResult>('/inventory/bulk-upload', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  /**
   * Get inventory items created from a specific purchase
   */
  async getItemsByPurchase(purchaseId: string): Promise<APIResponse<InventoryItem[]>> {
    inventoryLogger.debug('Fetching items for purchase', { purchaseId });
    return this.makeRequest<InventoryItem[]>(`/inventory?purchaseId=${encodeURIComponent(purchaseId)}`);
  }
}

// Export singleton instance
export const inventoryAPI = new InventoryAPI();
